import { COLORS, FONTS } from "../../../../../lib/brand";
import { PANE_THEMES } from "../../../../../components/brand/paneThemes";
import { useFocusEffect, useLocalSearchParams } from "expo-router";
import { useCallback, useMemo, useRef, useState } from "react";
import {
  ActivityIndicator,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  View,
  type TextStyle,
  type ViewStyle,
} from "react-native";

import { getClientCompetition } from "../../../../../lib/clientCompetitions";
import { useRequireApprovedCase } from "../../../../../lib/useRequireApprovedCase";
import {
  archivePosition,
  createPosition,
  linkPositionToCompetition,
  listAllPositionsForCompany,
  listPositionsForCompetition,
  unlinkPositionFromCompetition,
  updatePosition,
  type EmploymentType,
  type Position,
  type PositionInput,
  type PositionStatus,
  type PositionWithInterestCount,
} from "../../../../../lib/positions";

const THEME = PANE_THEMES.Client;
const FOREST_MID = COLORS.FOREST_MID.hex;
const FOREST_DEEP = COLORS.FOREST_DEEP.hex;
const AMBER = THEME.accent;
const SAGE = COLORS.SAGE.hex;
const LINEN = THEME.textOnSurface;
const FOREST = THEME.sidebarBg;

const W = "rgba(255,255,255,1)";
const WS = "rgba(255,255,255,0.78)";
const WM = "rgba(255,255,255,0.55)";
const WL = "rgba(255,255,255,0.28)";
const BORDER = "rgba(255,255,255,0.07)";
const RED = THEME.destructive;

const PAGE_MAX_W = 1100;

const EMPLOYMENT_LABELS: Record<EmploymentType, string> = {
  full_time: "Full-time",
  part_time: "Part-time",
  internship: "Internship",
  contract: "Contract",
};

const STATUS_LABELS: Record<PositionStatus, string> = {
  draft: "Draft",
  active: "Active",
  filled: "Filled",
  closed: "Closed",
};

function formatYearRange(min: number | null, max: number | null): string | null {
  if (min == null && max == null) return null;
  if (min != null && max != null) return min === max ? `Grads ${min}` : `Grads ${min}–${max}`;
  if (min != null) return `Grads ${min}+`;
  return `Grads up to ${max}`;
}

export default function ClientPositionsScreen() {
  // Route guard — pre-approval cases bounce to /edit (no positions yet).
  useRequireApprovedCase();
  const { id } = useLocalSearchParams<{ id: string }>();
  const competitionId = typeof id === "string" ? id : "";

  const [companyId, setCompanyId] = useState<string | null>(null);
  const [items, setItems] = useState<PositionWithInterestCount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<Position | null>(null);
  const [reuseOpen, setReuseOpen] = useState(false);

  const load = useCallback(async () => {
    if (!competitionId) return;
    setLoading(true);
    setError(null);
    try {
      const comp = await getClientCompetition(competitionId);
      setCompanyId(comp?.company_id ?? null);
      const list = await listPositionsForCompetition(competitionId);
      setItems(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load positions");
    } finally {
      setLoading(false);
    }
  }, [competitionId]);

  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load])
  );

  function openNew() {
    setEditing(null);
    setEditorOpen(true);
  }

  function openEdit(p: Position) {
    setEditing(p);
    setEditorOpen(true);
  }

  async function onSavePosition(input: PositionInput, existingId: string | null) {
    if (!companyId) return { error: new Error("Company not loaded yet — try again.") };
    if (existingId) {
      const { error } = await updatePosition(existingId, input);
      return { error };
    }
    const { id: newId, error } = await createPosition({ ...input, company_id: companyId });
    if (error || !newId) return { error };
    // Link the freshly-created position to this competition.
    const { error: linkErr } = await linkPositionToCompetition(newId, competitionId);
    return { error: linkErr };
  }

  async function onArchive(p: Position) {
    const { error: e } = await archivePosition(p.id);
    if (e) {
      setError(e.message);
      return;
    }
    void load();
  }

  async function onUnlink(p: Position) {
    const { error: e } = await unlinkPositionFromCompetition(p.id, competitionId);
    if (e) {
      setError(e.message);
      return;
    }
    void load();
  }

  async function onLinkExisting(positionIds: string[]) {
    for (const pid of positionIds) {
      const { error: e } = await linkPositionToCompetition(pid, competitionId);
      if (e) {
        setError(e.message);
        return;
      }
    }
    setReuseOpen(false);
    void load();
  }

  if (loading) {
    return (
      <View style={{ flex: 1, alignItems: "center", justifyContent: "center" }}>
        <ActivityIndicator color={AMBER} />
      </View>
    );
  }

  return (
    <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingBottom: 80 }}>
      <View style={{ paddingHorizontal: 32, paddingTop: 26, maxWidth: PAGE_MAX_W }}>
        <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
          <View style={{ width: 22, height: 2, backgroundColor: AMBER, opacity: 0.85 }} />
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 9.5,
              letterSpacing: 2,
              textTransform: "uppercase",
              color: AMBER,
            }}
          >
            Positions
          </Text>
        </View>
        <Text
          style={{
            marginTop: 6,
            fontFamily: FONTS.display,
            fontSize: 32,
            lineHeight: 36,
            color: W,
          }}
        >
          Roles you're hiring for
        </Text>
        <Text style={{ marginTop: 6, fontFamily: FONTS.body, fontSize: 13, color: WM, maxWidth: 720 }}>
          Add the open roles tied to this program. Students see them at registration and opt in to
          the ones they want to be considered for. Companies cannot see who opted in until after
          judging closes — that prevents pre-judgment from biasing scoring.
        </Text>

        {error ? (
          <View
            style={{
              marginTop: 14,
              paddingHorizontal: 14,
              paddingVertical: 12,
              borderRadius: 3,
              backgroundColor: "rgba(230,154,138,0.08)",
              borderWidth: 1,
              borderColor: "rgba(230,154,138,0.4)",
            }}
          >
            <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12.5, color: RED }}>{error}</Text>
          </View>
        ) : null}

        <View style={{ marginTop: 18, flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
          <Pressable
            onPress={openNew}
            style={[
              {
                paddingHorizontal: 18,
                paddingVertical: 10,
                borderRadius: 3,
                backgroundColor: AMBER,
              } as ViewStyle,
              Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
            ]}
          >
            <Text
              style={{
                fontFamily: FONTS.bodyBold,
                fontSize: 11,
                letterSpacing: 1.6,
                textTransform: "uppercase",
                color: FOREST,
              }}
            >
              + Add position
            </Text>
          </Pressable>
          <Pressable
            onPress={() => setReuseOpen(true)}
            style={[
              {
                paddingHorizontal: 16,
                paddingVertical: 10,
                borderRadius: 3,
                borderWidth: 1,
                borderColor: BORDER,
                backgroundColor: "rgba(255,255,255,0.02)",
              } as ViewStyle,
              Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
            ]}
          >
            <Text
              style={{
                fontFamily: FONTS.bodyBold,
                fontSize: 11,
                letterSpacing: 1.4,
                textTransform: "uppercase",
                color: WS,
              }}
            >
              Reuse existing
            </Text>
          </Pressable>
        </View>

        {items.length === 0 ? (
          <View
            style={{
              marginTop: 22,
              paddingVertical: 32,
              paddingHorizontal: 22,
              borderRadius: 6,
              borderWidth: 1,
              borderStyle: "dashed",
              borderColor: BORDER,
              alignItems: "center",
            }}
          >
            <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 18, color: LINEN }}>
              No positions yet
            </Text>
            <Text
              style={{
                marginTop: 6,
                fontFamily: FONTS.body,
                fontSize: 12.5,
                color: WM,
                textAlign: "center",
                maxWidth: 420,
              }}
            >
              Add a position to start collecting candidate interests at registration. Students see
              the roles tied to this program and choose which ones they want to be considered for.
            </Text>
          </View>
        ) : (
          <View style={{ marginTop: 22, gap: 14 }}>
            {items.map((p) => (
              <PositionCard
                key={p.id}
                position={p}
                onEdit={() => openEdit(p)}
                onArchive={() => void onArchive(p)}
                onUnlink={() => void onUnlink(p)}
              />
            ))}
          </View>
        )}
      </View>

      <PositionEditorModal
        visible={editorOpen}
        initial={editing}
        onClose={() => setEditorOpen(false)}
        onSave={async (input) => {
          const { error: e } = await onSavePosition(input, editing?.id ?? null);
          if (e) {
            setError(e.message);
            return false;
          }
          setEditorOpen(false);
          void load();
          return true;
        }}
      />

      {reuseOpen && companyId ? (
        <ReuseExistingModal
          companyId={companyId}
          existingIds={items.map((p) => p.id)}
          onClose={() => setReuseOpen(false)}
          onSelect={onLinkExisting}
        />
      ) : null}
    </ScrollView>
  );
}

function PositionCard({
  position,
  onEdit,
  onArchive,
  onUnlink,
}: {
  position: PositionWithInterestCount;
  onEdit: () => void;
  onArchive: () => void;
  onUnlink: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [archiveArmed, setArchiveArmed] = useState(false);
  const [unlinkArmed, setUnlinkArmed] = useState(false);

  const yearRange = formatYearRange(position.grad_year_min, position.grad_year_max);
  const eligChips: string[] = [];
  if (yearRange) eligChips.push(yearRange);
  if (position.eligible_majors.length > 0)
    eligChips.push(position.eligible_majors.slice(0, 3).join(" / "));
  if (position.eligible_universities.length > 0)
    eligChips.push(position.eligible_universities.slice(0, 3).join(" / "));
  if (position.us_citizen_required) eligChips.push("US citizen required");
  if (position.gpa_min != null) eligChips.push("GPA " + position.gpa_min.toFixed(1) + "+");

  const statusTint =
    position.status === "active"
      ? SAGE
      : position.status === "filled"
        ? AMBER
        : position.status === "closed"
          ? WL
          : WM;

  const description = position.description?.trim() ?? "";
  const showExpand = description.length > 200;
  const visibleDesc = expanded || !showExpand ? description : description.slice(0, 200) + "…";

  return (
    <View
      style={{
        padding: 18,
        borderRadius: 6,
        backgroundColor: FOREST_DEEP,
        borderWidth: 1,
        borderColor: BORDER,
      }}
    >
      <View style={{ flexDirection: "row", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
        <Text style={{ flex: 1, fontFamily: FONTS.displayMedium, fontSize: 22, color: LINEN }}>
          {position.title}
        </Text>
        <View
          style={{
            paddingHorizontal: 9,
            paddingVertical: 3,
            borderRadius: 999,
            borderWidth: 1,
            borderColor: statusTint,
          }}
        >
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 9,
              letterSpacing: 1.4,
              textTransform: "uppercase",
              color: statusTint,
            }}
          >
            {STATUS_LABELS[position.status]}
          </Text>
        </View>
      </View>

      <Text style={{ marginTop: 6, fontFamily: FONTS.body, fontSize: 12, color: WM }}>
        {[position.location, position.employment_type ? EMPLOYMENT_LABELS[position.employment_type] : null, position.comp_band]
          .filter((v) => !!v)
          .join(" · ") || "—"}
      </Text>

      {description ? (
        <View style={{ marginTop: 10 }}>
          <Text style={{ fontFamily: FONTS.body, fontSize: 12.5, color: WS, lineHeight: 19 }}>
            {visibleDesc}
          </Text>
          {showExpand ? (
            <Pressable
              onPress={() => setExpanded((v) => !v)}
              style={Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null}
            >
              <Text
                style={{
                  marginTop: 4,
                  fontFamily: FONTS.bodyBold,
                  fontSize: 10.5,
                  letterSpacing: 1.2,
                  textTransform: "uppercase",
                  color: AMBER,
                }}
              >
                {expanded ? "Show less" : "Show more"}
              </Text>
            </Pressable>
          ) : null}
        </View>
      ) : null}

      {eligChips.length > 0 ? (
        <View style={{ marginTop: 12, flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
          {eligChips.map((c, i) => (
            <View
              key={i}
              style={{
                paddingHorizontal: 9,
                paddingVertical: 4,
                borderRadius: 999,
                backgroundColor: "rgba(255,255,255,0.04)",
                borderWidth: 1,
                borderColor: BORDER,
              }}
            >
              <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 10.5, color: WS }}>{c}</Text>
            </View>
          ))}
        </View>
      ) : null}

      <View
        style={{
          marginTop: 14,
          paddingTop: 12,
          borderTopWidth: 1,
          borderTopColor: BORDER,
          flexDirection: "row",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <Text style={{ flex: 1, fontFamily: FONTS.bodyMedium, fontSize: 12, color: WS }}>
          {position.active_interest_count}{" "}
          {position.active_interest_count === 1 ? "student" : "students"} opted in
        </Text>
        <Pressable
          onPress={onEdit}
          style={Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null}
        >
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 10.5,
              letterSpacing: 1.4,
              textTransform: "uppercase",
              color: AMBER,
            }}
          >
            Edit
          </Text>
        </Pressable>
        <Pressable
          onPress={() => {
            if (unlinkArmed) {
              setUnlinkArmed(false);
              onUnlink();
            } else {
              setUnlinkArmed(true);
              setTimeout(() => setUnlinkArmed(false), 4000);
            }
          }}
          style={Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null}
        >
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 10.5,
              letterSpacing: 1.4,
              textTransform: "uppercase",
              color: WS,
            }}
          >
            {unlinkArmed ? "Click again — remove" : "Unlink"}
          </Text>
        </Pressable>
        {position.status !== "closed" ? (
          <Pressable
            onPress={() => {
              if (archiveArmed) {
                setArchiveArmed(false);
                onArchive();
              } else {
                setArchiveArmed(true);
                setTimeout(() => setArchiveArmed(false), 4000);
              }
            }}
            style={Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null}
          >
            <Text
              style={{
                fontFamily: FONTS.bodyBold,
                fontSize: 10.5,
                letterSpacing: 1.4,
                textTransform: "uppercase",
                color: RED,
              }}
            >
              {archiveArmed ? "Click again — archive" : "Archive"}
            </Text>
          </Pressable>
        ) : null}
      </View>
    </View>
  );
}

function PositionEditorModal({
  visible,
  initial,
  onClose,
  onSave,
}: {
  visible: boolean;
  initial: Position | null;
  onClose: () => void;
  onSave: (input: PositionInput) => Promise<boolean>;
}) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState("");
  const [employmentType, setEmploymentType] = useState<EmploymentType | "">("");
  const [compBand, setCompBand] = useState("");
  const [gradMin, setGradMin] = useState("");
  const [gradMax, setGradMax] = useState("");
  const [majors, setMajors] = useState<string[]>([]);
  const [universities, setUniversities] = useState<string[]>([]);
  const [usCitizen, setUsCitizen] = useState(false);
  const [gpaMin, setGpaMin] = useState("");
  const [status, setStatus] = useState<PositionStatus>("active");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const lastInitRef = useRef<string | null>(null);
  // Only re-initialize when the modal is opened or the editing target changes.
  if (visible) {
    const key = initial?.id ?? "__new__";
    if (lastInitRef.current !== key) {
      lastInitRef.current = key;
      setTitle(initial?.title ?? "");
      setDescription(initial?.description ?? "");
      setLocation(initial?.location ?? "");
      setEmploymentType((initial?.employment_type as EmploymentType) ?? "");
      setCompBand(initial?.comp_band ?? "");
      setGradMin(initial?.grad_year_min != null ? String(initial.grad_year_min) : "");
      setGradMax(initial?.grad_year_max != null ? String(initial.grad_year_max) : "");
      setMajors(initial?.eligible_majors ?? []);
      setUniversities(initial?.eligible_universities ?? []);
      setUsCitizen(initial?.us_citizen_required ?? false);
      setGpaMin(initial?.gpa_min != null ? String(initial.gpa_min) : "");
      setStatus(initial?.status ?? "active");
      setErr(null);
    }
  } else if (lastInitRef.current !== null) {
    lastInitRef.current = null;
  }

  async function save() {
    if (!title.trim()) {
      setErr("Title is required.");
      return;
    }
    setSaving(true);
    setErr(null);
    const input: PositionInput = {
      // company_id is filled in by the caller (parent page knows the comp
      // and resolves company from it).
      company_id: "",
      title: title.trim(),
      description: description.trim() || null,
      location: location.trim() || null,
      employment_type: employmentType ? (employmentType as EmploymentType) : null,
      comp_band: compBand.trim() || null,
      grad_year_min: gradMin.trim() ? Number(gradMin) : null,
      grad_year_max: gradMax.trim() ? Number(gradMax) : null,
      eligible_majors: majors,
      eligible_universities: universities,
      us_citizen_required: usCitizen,
      gpa_min: gpaMin.trim() ? Number(gpaMin) : null,
      status,
    };
    const ok = await onSave(input);
    setSaving(false);
    if (!ok) setErr("Could not save the position. Try again.");
  }

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View
        style={{
          flex: 1,
          backgroundColor: "rgba(0,0,0,0.65)",
          justifyContent: "center",
          paddingHorizontal: 16,
        }}
      >
        <View
          style={{
            maxWidth: 640,
            width: "100%",
            maxHeight: "92%",
            alignSelf: "center",
            backgroundColor: FOREST_MID,
            borderRadius: 8,
            borderWidth: 1,
            borderColor: BORDER,
          }}
        >
          <ScrollView contentContainerStyle={{ padding: 22 }}>
            <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 22, color: LINEN }}>
              {initial ? "Edit position" : "Add position"}
            </Text>

            {err ? (
              <View
                style={{
                  marginTop: 12,
                  paddingHorizontal: 12,
                  paddingVertical: 10,
                  borderRadius: 3,
                  backgroundColor: "rgba(230,154,138,0.08)",
                  borderWidth: 1,
                  borderColor: "rgba(230,154,138,0.4)",
                }}
              >
                <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12, color: RED }}>{err}</Text>
              </View>
            ) : null}

            <FieldLabel required>Title</FieldLabel>
            <DarkInput value={title} onChange={setTitle} placeholder="Summer Analyst" />

            <FieldLabel>Description</FieldLabel>
            <DarkInput
              value={description}
              onChange={setDescription}
              placeholder="What the role involves, who they'd work with, what success looks like."
              multiline
            />

            <FieldLabel>Location</FieldLabel>
            <DarkInput
              value={location}
              onChange={setLocation}
              placeholder="Remote · Gainesville, FL · Hybrid (NYC)"
            />

            <FieldLabel>Employment type</FieldLabel>
            <View style={{ marginTop: 6, flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
              {(Object.keys(EMPLOYMENT_LABELS) as EmploymentType[]).map((t) => {
                const selected = employmentType === t;
                return (
                  <Pressable
                    key={t}
                    onPress={() => setEmploymentType(selected ? "" : t)}
                    style={[
                      {
                        paddingHorizontal: 12,
                        paddingVertical: 7,
                        borderRadius: 999,
                        borderWidth: 1,
                        borderColor: selected ? AMBER : BORDER,
                        backgroundColor: selected ? "rgba(181,135,58,0.16)" : "rgba(255,255,255,0.02)",
                      } as ViewStyle,
                      Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                    ]}
                  >
                    <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11.5, color: selected ? AMBER : WS }}>
                      {EMPLOYMENT_LABELS[t]}
                    </Text>
                  </Pressable>
                );
              })}
            </View>

            <FieldLabel>Comp band (optional)</FieldLabel>
            <DarkInput value={compBand} onChange={setCompBand} placeholder="$70k–$90k" />

            <FieldLabel>Graduation year range (optional)</FieldLabel>
            <View style={{ marginTop: 6, flexDirection: "row", gap: 8 }}>
              <View style={{ flex: 1 }}>
                <DarkInput
                  value={gradMin}
                  onChange={setGradMin}
                  placeholder="Min, e.g. 2026"
                  keyboardType="number-pad"
                />
              </View>
              <View style={{ flex: 1 }}>
                <DarkInput
                  value={gradMax}
                  onChange={setGradMax}
                  placeholder="Max, e.g. 2028"
                  keyboardType="number-pad"
                />
              </View>
            </View>

            <FieldLabel hint="Empty = all majors eligible">Eligible majors</FieldLabel>
            <ChipsInput values={majors} onChange={setMajors} placeholder="Type a major and press Enter" />

            <FieldLabel hint="Empty = all schools eligible">Eligible universities</FieldLabel>
            <ChipsInput
              values={universities}
              onChange={setUniversities}
              placeholder="Type a school and press Enter"
            />

            <View style={{ marginTop: 16, flexDirection: "row", alignItems: "center", gap: 12 }}>
              <Pressable
                onPress={() => setUsCitizen((v) => !v)}
                style={[
                  {
                    paddingHorizontal: 12,
                    paddingVertical: 7,
                    borderRadius: 3,
                    borderWidth: 1,
                    borderColor: usCitizen ? AMBER : BORDER,
                    backgroundColor: usCitizen ? "rgba(181,135,58,0.12)" : "rgba(255,255,255,0.02)",
                  } as ViewStyle,
                  Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                ]}
              >
                <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12, color: usCitizen ? AMBER : WS }}>
                  {usCitizen ? "✓ US citizen required" : "US citizen required"}
                </Text>
              </Pressable>
              <View style={{ flex: 1 }}>
                <FieldLabel>GPA minimum</FieldLabel>
                <DarkInput value={gpaMin} onChange={setGpaMin} placeholder="e.g. 3.0" keyboardType="decimal-pad" />
              </View>
            </View>

            <FieldLabel>Status</FieldLabel>
            <View style={{ marginTop: 6, flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
              {(Object.keys(STATUS_LABELS) as PositionStatus[]).map((s) => {
                const selected = status === s;
                return (
                  <Pressable
                    key={s}
                    onPress={() => setStatus(s)}
                    style={[
                      {
                        paddingHorizontal: 12,
                        paddingVertical: 7,
                        borderRadius: 999,
                        borderWidth: 1,
                        borderColor: selected ? AMBER : BORDER,
                        backgroundColor: selected ? "rgba(181,135,58,0.16)" : "rgba(255,255,255,0.02)",
                      } as ViewStyle,
                      Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                    ]}
                  >
                    <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11.5, color: selected ? AMBER : WS }}>
                      {STATUS_LABELS[s]}
                    </Text>
                  </Pressable>
                );
              })}
            </View>

            <View style={{ marginTop: 22, flexDirection: "row", justifyContent: "flex-end", gap: 14 }}>
              <Pressable
                onPress={onClose}
                style={[
                  { paddingHorizontal: 14, paddingVertical: 10 },
                  Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                ]}
              >
                <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12, color: WM }}>Cancel</Text>
              </Pressable>
              <Pressable
                onPress={() => void save()}
                disabled={saving}
                style={[
                  {
                    paddingHorizontal: 18,
                    paddingVertical: 10,
                    borderRadius: 3,
                    backgroundColor: AMBER,
                    opacity: saving ? 0.5 : 1,
                  } as ViewStyle,
                  Platform.OS === "web" && !saving ? ({ cursor: "pointer" } as const) : null,
                ]}
              >
                {saving ? (
                  <ActivityIndicator size="small" color={FOREST} />
                ) : (
                  <Text
                    style={{
                      fontFamily: FONTS.bodyBold,
                      fontSize: 11.5,
                      letterSpacing: 1.4,
                      textTransform: "uppercase",
                      color: FOREST,
                    }}
                  >
                    {initial ? "Save changes" : "Add position"}
                  </Text>
                )}
              </Pressable>
            </View>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
}

function ReuseExistingModal({
  companyId,
  existingIds,
  onClose,
  onSelect,
}: {
  companyId: string;
  existingIds: string[];
  onClose: () => void;
  onSelect: (positionIds: string[]) => Promise<void>;
}) {
  const [all, setAll] = useState<Position[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [linking, setLinking] = useState(false);

  useFocusEffect(
    useCallback(() => {
      let alive = true;
      void (async () => {
        setLoading(true);
        try {
          const list = await listAllPositionsForCompany(companyId);
          if (alive) setAll(list);
        } finally {
          if (alive) setLoading(false);
        }
      })();
      return () => {
        alive = false;
      };
    }, [companyId])
  );

  const candidates = useMemo(
    () => all.filter((p) => !existingIds.includes(p.id) && p.status !== "closed"),
    [all, existingIds]
  );

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function attach() {
    if (selected.size === 0) return;
    setLinking(true);
    await onSelect(Array.from(selected));
    setLinking(false);
  }

  return (
    <Modal visible transparent animationType="fade" onRequestClose={onClose}>
      <View
        style={{
          flex: 1,
          backgroundColor: "rgba(0,0,0,0.65)",
          justifyContent: "center",
          paddingHorizontal: 16,
        }}
      >
        <View
          style={{
            maxWidth: 560,
            width: "100%",
            maxHeight: "82%",
            alignSelf: "center",
            backgroundColor: FOREST_MID,
            borderRadius: 8,
            borderWidth: 1,
            borderColor: BORDER,
            padding: 22,
          }}
        >
          <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 22, color: LINEN }}>
            Reuse a position
          </Text>
          <Text style={{ marginTop: 4, fontFamily: FONTS.body, fontSize: 12, color: WM }}>
            Pick one or more roles you've already created on a previous program.
          </Text>

          <ScrollView style={{ marginTop: 14, maxHeight: 360 }}>
            {loading ? (
              <ActivityIndicator color={AMBER} />
            ) : candidates.length === 0 ? (
              <Text style={{ fontFamily: FONTS.body, fontSize: 12.5, color: WL }}>
                No reusable positions on file. Create a new one above.
              </Text>
            ) : (
              <View style={{ gap: 6 }}>
                {candidates.map((p) => {
                  const checked = selected.has(p.id);
                  return (
                    <Pressable
                      key={p.id}
                      onPress={() => toggle(p.id)}
                      style={[
                        {
                          paddingHorizontal: 14,
                          paddingVertical: 12,
                          borderRadius: 4,
                          borderWidth: 1,
                          borderColor: checked ? AMBER : BORDER,
                          backgroundColor: checked ? "rgba(181,135,58,0.10)" : "rgba(255,255,255,0.02)",
                          flexDirection: "row",
                          gap: 12,
                          alignItems: "center",
                        } as ViewStyle,
                        Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                      ]}
                    >
                      <View
                        style={{
                          width: 18,
                          height: 18,
                          borderRadius: 3,
                          borderWidth: 1.5,
                          borderColor: checked ? AMBER : BORDER,
                          backgroundColor: checked ? AMBER : "transparent",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {checked ? (
                          <Text style={{ fontSize: 11, color: FOREST, fontFamily: FONTS.bodyBold }}>✓</Text>
                        ) : null}
                      </View>
                      <View style={{ flex: 1 }}>
                        <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 13, color: W }}>{p.title}</Text>
                        <Text
                          style={{ marginTop: 2, fontFamily: FONTS.body, fontSize: 11, color: WM }}
                          numberOfLines={1}
                        >
                          {[p.location, p.employment_type ? EMPLOYMENT_LABELS[p.employment_type] : null, STATUS_LABELS[p.status]]
                            .filter((v) => !!v)
                            .join(" · ")}
                        </Text>
                      </View>
                    </Pressable>
                  );
                })}
              </View>
            )}
          </ScrollView>

          <View style={{ marginTop: 18, flexDirection: "row", justifyContent: "flex-end", gap: 14 }}>
            <Pressable
              onPress={onClose}
              style={[
                { paddingHorizontal: 14, paddingVertical: 10 },
                Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
              ]}
            >
              <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12, color: WM }}>Cancel</Text>
            </Pressable>
            <Pressable
              onPress={() => void attach()}
              disabled={linking || selected.size === 0}
              style={[
                {
                  paddingHorizontal: 18,
                  paddingVertical: 10,
                  borderRadius: 3,
                  backgroundColor: selected.size > 0 ? AMBER : "rgba(181,135,58,0.30)",
                  opacity: linking ? 0.5 : 1,
                } as ViewStyle,
                Platform.OS === "web" && selected.size > 0 ? ({ cursor: "pointer" } as const) : null,
              ]}
            >
              <Text
                style={{
                  fontFamily: FONTS.bodyBold,
                  fontSize: 11,
                  letterSpacing: 1.4,
                  textTransform: "uppercase",
                  color: selected.size > 0 ? FOREST : WS,
                }}
              >
                {linking ? "Linking…" : `Link ${selected.size || ""} → this program`}
              </Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
}

function FieldLabel({
  children,
  required,
  hint,
}: {
  children: string;
  required?: boolean;
  hint?: string;
}) {
  return (
    <View style={{ marginTop: 14 }}>
      <View style={{ flexDirection: "row", alignItems: "center", gap: 4 }}>
        <Text
          style={{
            fontFamily: FONTS.bodyBold,
            fontSize: 9.5,
            letterSpacing: 1.6,
            textTransform: "uppercase",
            color: WM,
          }}
        >
          {children}
        </Text>
        {required ? <Text style={{ color: RED, fontSize: 10 }}>*</Text> : null}
      </View>
      {hint ? (
        <Text style={{ marginTop: 2, fontFamily: FONTS.body, fontSize: 10.5, color: WL }}>
          {hint}
        </Text>
      ) : null}
    </View>
  );
}

function DarkInput({
  value,
  onChange,
  placeholder,
  multiline,
  keyboardType,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  multiline?: boolean;
  keyboardType?: "default" | "number-pad" | "decimal-pad" | "email-address";
}) {
  return (
    <TextInput
      value={value}
      onChangeText={onChange}
      placeholder={placeholder}
      placeholderTextColor={WL}
      multiline={multiline}
      keyboardType={keyboardType}
      style={
        {
          marginTop: 6,
          minHeight: multiline ? 90 : undefined,
          backgroundColor: FOREST_DEEP,
          borderWidth: 1,
          borderColor: BORDER,
          borderRadius: 4,
          paddingHorizontal: 12,
          paddingVertical: 10,
          color: W,
          fontFamily: FONTS.body,
          fontSize: 13,
          textAlignVertical: multiline ? "top" : undefined,
          ...(Platform.OS === "web" ? ({ outlineStyle: "none" } as object) : {}),
        } as unknown as TextStyle
      }
    />
  );
}

function ChipsInput({
  values,
  onChange,
  placeholder,
}: {
  values: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
}) {
  const [draft, setDraft] = useState("");

  function commit() {
    const v = draft.trim();
    if (!v) return;
    if (values.some((x) => x.toLowerCase() === v.toLowerCase())) {
      setDraft("");
      return;
    }
    onChange([...values, v]);
    setDraft("");
  }

  function remove(idx: number) {
    onChange(values.filter((_, i) => i !== idx));
  }

  return (
    <View
      style={{
        marginTop: 6,
        backgroundColor: FOREST_DEEP,
        borderWidth: 1,
        borderColor: BORDER,
        borderRadius: 4,
        paddingHorizontal: 8,
        paddingVertical: 6,
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 6,
      }}
    >
      {values.map((v, i) => (
        <Pressable
          key={i}
          onPress={() => remove(i)}
          style={[
            {
              paddingHorizontal: 9,
              paddingVertical: 4,
              borderRadius: 999,
              backgroundColor: "rgba(181,135,58,0.16)",
              flexDirection: "row",
              alignItems: "center",
              gap: 6,
            } as ViewStyle,
            Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
          ]}
        >
          <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11.5, color: AMBER }}>{v}</Text>
          <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11, color: WM }}>×</Text>
        </Pressable>
      ))}
      <TextInput
        value={draft}
        onChangeText={setDraft}
        onSubmitEditing={commit}
        onBlur={commit}
        placeholder={values.length === 0 ? placeholder : ""}
        placeholderTextColor={WL}
        style={
          {
            flex: 1,
            minWidth: 140,
            color: W,
            fontFamily: FONTS.body,
            fontSize: 12.5,
            paddingVertical: 4,
            ...(Platform.OS === "web" ? ({ outlineStyle: "none" } as object) : {}),
          } as unknown as TextStyle
        }
      />
    </View>
  );
}
